import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { startWithEmptyList } from '@freelancer/datastore';
import { Language, LanguageDetect } from '@freelancer/datastore/collections';
import { BannerAlertType } from '@freelancer/ui/banner-alert';
import { HeadingType } from '@freelancer/ui/heading';
import { Margin } from '@freelancer/ui/margin';
import { SelectItem, SelectSize } from '@freelancer/ui/select';
import { FontColor, TextSize } from '@freelancer/ui/text';
import * as Rx from 'rxjs';
import { map, startWith, tap } from 'rxjs/operators';
import { JobType } from '../job-type/job-type.component';

export const DESCRIPTION_MAX_CHARACTERS = 4000;
export const DESCRIPTION_MIN_CHARACTERS = 30;

const REVIEW_VIOLATION_TERMS: ReadonlyArray<string> = [
  'followers',
  'follows',
  'likes',
  'retweet',
  'retweets',
  'review',
  'reviews',
  'share',
  'shares',
  'tweet',
  'tweets',
];

const REVIEW_VIOLATION_SITES: ReadonlyArray<string> = [
  'amazon',
  'city search',
  'citysearch',
  'ebay',
  'expedia',
  'facebook',
  'fb',
  'google',
  'ig',
  'instagram',
  'trip advisor',
  'tripadvisor',
  'twitter',
  'yelp',
];

@Component({
  selector: 'app-description',
  template: `
    <ng-container [ngSwitch]="jobType">
      <fl-heading
        *ngSwitchCase="JobType.CONTEST"
        i18n="Post Job Page description field label"
        [headingType]="HeadingType.H3"
        [flMarginBottom]="Margin.XXXSMALL"
        [size]="TextSize.MID"
      >
        Describe your contest in detail
      </fl-heading>
      <fl-heading
        *ngSwitchDefault
        i18n="Post Job Page description field label"
        [headingType]="HeadingType.H3"
        [flMarginBottom]="Margin.XXXSMALL"
        [size]="TextSize.MID"
      >
        Tell us more about your project
      </fl-heading>
    </ng-container>
    <fl-text
      i18n="Post Job Page form description field description"
      [flMarginBottom]="Margin.SMALL"
    >
      Start with a bit about yourself or your business, and include an overview
      of what you need done.
    </fl-text>
    <fl-bit [flMarginBottom]="Margin.MID">
      <ng-container [ngSwitch]="jobType">
        <fl-textarea
          *ngSwitchCase="JobType.CONTEST"
          flTrackingLabel="TextInputField"
          i18n-placeholder="Post Job Page form description field placeholder"
          placeholder="Describe your contest here..."
          [control]="control"
          [rows]="4"
        ></fl-textarea>
        <fl-textarea
          *ngSwitchDefault
          flTrackingLabel="TextInputField"
          i18n-placeholder="Post Job Page form description field placeholder"
          placeholder="Describe your project here..."
          [control]="control"
          [rows]="4"
        ></fl-textarea>
      </ng-container>
      <fl-bit>
        <fl-text
          class="CharacterCountContainer"
          i18n="Post Job Page form description character counter"
          [flMarginBottom]="Margin.XXSMALL"
          [size]="TextSize.XXSMALL"
          [color]="FontColor.MID"
        >
          {{ DESCRIPTION_MAX_CHARACTERS - control?.value.length }} characters
          remaining
        </fl-text>
        <ng-container
          *ngIf="detectedLanguage$ | async as detectedLanguage"
          flTrackingSection="PJPLanguageDetection"
        >
          <fl-text
            i18n="Post Job Page form description detected language"
            [ngClass]="{ IsHidden: showLanguageSelector }"
          >
            Detected language: {{ detectedLanguage.name }}. If we are wrong
            <fl-link
              flTrackingLabel="WrongLanguage"
              [flTrackingExtraParams]="{ value: detectedLanguage.code }"
              (click)="onShowLanguageSelector()"
            >
              click here
            </fl-link>
          </fl-text>
          <fl-bit
            class="SelectLanguageContainer"
            [ngClass]="{ IsHidden: !showLanguageSelector }"
          >
            <fl-text
              i18n="Post Job Page form description's detected language"
              [flMarginRight]="Margin.XXXSMALL"
            >
              The description is written in
            </fl-text>
            <fl-select
              flTrackingLabel="SelectLanguage"
              [control]="languageControl"
              [options]="languageSelectItems$ | async"
            ></fl-select>
          </fl-bit>
        </ng-container>
      </fl-bit>
    </fl-bit>
    <fl-bit
      flTrackingSection="PJPDescriptionWarning"
      [flMarginBottom]="Margin.SMALL"
    >
      <fl-banner-alert
        *ngIf="hasCraigslistError$ | async"
        bannerTitle="Warning!"
        i18n="
           Post Job Page form description field craigslist TOU violation warning

        "
        i18n-bannerTitle="craigslist TOU violation warning banner title"
        [closeable]="false"
        [flMarginBottom]="Margin.XXSMALL"
        [type]="BannerAlertType.WARNING"
      >
        Craigslist, Inc. has advised that certain users might be soliciting
        others to violate Craigslist's
        <fl-link
          flTrackingLabel="Craigslist-TermsOfUse"
          [link]="'http://www.craigslist.org/about/terms.of.use'"
        >
          Terms of Use</fl-link
        >. This message is being displayed based on a list of keywords that
        Craigslist has provided us. Please ensure you do not violate
        Craigslist's TOU.
      </fl-banner-alert>
      <fl-banner-alert
        i18n="Post Job Page form description field copyright violation warning"
        i18n-bannerTitle="copyright violation warning banner title"
        *ngIf="hasCopyrightError$ | async"
        bannerTitle="Warning!"
        [type]="BannerAlertType.WARNING"
        [closeable]="false"
        [flMarginBottom]="Margin.XXSMALL"
      >
        It is illegal and against our terms of service to violate the
        intellectual property rights of others. Requesting exact copies or
        clones of items may be a violation if you are not the copyright holder,
        not authorised to do so by such, or not otherwise permitted by law.
      </fl-banner-alert>
      <fl-banner-alert
        i18n-bannerTitle="Reviews violation warning banner title"
        bannerTitle="Warning about fake social engagement"
        *ngIf="hasReviewBlacklistTerms$ | async"
        [closeable]="false"
        [flMarginBottom]="Margin.XXSMALL"
        [type]="BannerAlertType.WARNING"
      >
        <ng-container [ngSwitch]="jobType">
          <ng-container *ngSwitchCase="JobType.CONTEST">
            <fl-text
              i18n="
                 Post Job Page form description field reviews violation warning
              "
              [flMarginBottom]="Margin.XXSMALL"
            >
              Contests for creating fake social engagement, including reviews,
              likes, and follows, are strictly prohibited and are in violation
              of our
              <fl-link
                flTrackingLabel="FakeSocialEngagement-TermsAndConditions"
                link="/about/terms"
                [newTab]="true"
                >Terms and Conditions</fl-link
              >. Posting such contests will result in your account being
              suspended or terminated.
            </fl-text>

            <fl-checkbox
              i18n-label="
                 Post Job Page form description fake social engagement violation
                confirmation
              "
              label="I confirm that this contest is not for creating fake social engagement"
              [control]="fraudReviewsControl"
              [flMarginBottom]="Margin.XXSMALL"
              [flTrackingLabel]="
                fraudReviewsControl?.value
                  ? 'FakeSocialEngagement-Uncheck'
                  : 'FakeSocialEngagement-Check'
              "
            ></fl-checkbox>
          </ng-container>
          <ng-container *ngSwitchDefault>
            <fl-text
              i18n="
                 Post Job Page form description field reviews violation warning
              "
              [flMarginBottom]="Margin.XXSMALL"
            >
              Projects for creating fake social engagement, including reviews,
              likes, and follows, are strictly prohibited and are in violation
              of our
              <fl-link
                flTrackingLabel="FakeSocialEngagement-TermsAndConditions"
                link="/about/terms"
                [newTab]="true"
                >Terms and Conditions</fl-link
              >. Posting such projects will result in your account being
              suspended or terminated.
            </fl-text>

            <fl-checkbox
              i18n-label="
                 Post Job Page form description fake social engagement violation
                confirmation
              "
              label="I confirm that this project is not for creating fake social engagement"
              [control]="fraudReviewsControl"
              [flMarginBottom]="Margin.XXSMALL"
              [flTrackingLabel]="
                fraudReviewsControl?.value
                  ? 'FakeSocialEngagement-Uncheck'
                  : 'FakeSocialEngagement-Check'
              "
            ></fl-checkbox>
          </ng-container>
        </ng-container>
      </fl-banner-alert>
    </fl-bit>
  `,
  styleUrls: ['./description.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DescriptionComponent implements OnInit {
  BannerAlertType = BannerAlertType;
  DESCRIPTION_MAX_CHARACTERS = DESCRIPTION_MAX_CHARACTERS;
  FontColor = FontColor;
  TextSize = TextSize;
  HeadingType = HeadingType;
  JobType = JobType;
  Margin = Margin;
  SelectSize = SelectSize;

  @Input() control: FormControl;
  @Input() detectedLanguage$: Rx.Observable<LanguageDetect>;
  @Input() jobType: JobType;
  @Input() fraudReviewsControl: FormControl;
  @Input() languageControl: FormControl;
  @Input() languages$: Rx.Observable<ReadonlyArray<Language>>;

  hasCopyrightError$: Rx.Observable<boolean>;
  hasCraigslistError$: Rx.Observable<boolean>;
  hasReviewBlacklistTerms$: Rx.Observable<boolean>;
  languageSelectItems$: Rx.Observable<ReadonlyArray<SelectItem>>;

  showLanguageSelector = false;

  ngOnInit() {
    this.languageSelectItems$ = this.languages$.pipe(
      map(languages =>
        languages.map(language => ({
          value: language.code,
          displayText: language.name,
        })),
      ),
      startWithEmptyList(),
    );

    this.hasCraigslistError$ = this.control.statusChanges.pipe(
      map(() => !!this.control.getError('CRAIGSLIST_TOU_ERROR')),
    );

    this.hasCopyrightError$ = this.control.statusChanges.pipe(
      map(() => !!this.control.getError('COPYRIGHT_ERROR')),
    );

    const patterns = [
      new RegExp(REVIEW_VIOLATION_SITES.join('|'), 'i'),
      new RegExp(REVIEW_VIOLATION_TERMS.join('|'), 'i'),
    ];

    this.hasReviewBlacklistTerms$ = this.control.valueChanges.pipe(
      startWith(this.control.value),
      map(value =>
        patterns
          .map(pattern => value.match(pattern))
          .every(matches => matches && matches.length > 0),
      ),
      tap(hasReviewBlacklistTerms =>
        hasReviewBlacklistTerms
          ? this.fraudReviewsControl.enable()
          : this.fraudReviewsControl.disable(),
      ),
    );
  }

  onShowLanguageSelector() {
    this.showLanguageSelector = true;
    this.languageControl.markAsTouched();
  }
}
